import * as Sentry from '@sentry/react';
import type { Breadcrumb, ErrorEvent, EventHint } from '@sentry/react';
import type { ErrorInfo } from 'react';

import { env } from '@/lib/env';
import { GmailError, InvalidDraftError } from '@/lib/gmail/errors';

const GMAIL_ERROR_NAMES = new Set(['GmailError', 'InvalidDraftError']);
const GMAIL_MODULE = /\/src\/lib\/gmail\/|\/src\/repository\/gmail\.ts\b/;
const GOOGLE_DOMAINS = ['google.com', 'googleapis.com'];
const GOOGLE_TEXT = /googleapis\.com|google\.com|\bgmail:/i;

// Frames carry source paths in dev only; a prod bundle leaves the class check to catch it.
export const isGmailModuleFile = (filename: string): boolean =>
  GMAIL_MODULE.test(filename);

const isGmailError = (value: unknown): boolean =>
  value instanceof GmailError ||
  value instanceof InvalidDraftError ||
  (value instanceof Error && GMAIL_ERROR_NAMES.has(value.name));

const isGoogleUrl = (url: unknown): boolean => {
  if (typeof url !== 'string') return false;
  try {
    const host = new URL(url, window.location.origin).hostname;
    return GOOGLE_DOMAINS.some(
      (domain) => host === domain || host.endsWith(`.${domain}`),
    );
  } catch {
    return false;
  }
};

const mentionsGmail = (value: unknown): boolean =>
  isGmailError(value) || (typeof value === 'string' && GOOGLE_TEXT.test(value));

// Gmail data never reaches Sentry: neither a Gmail error nor a frame inside the Gmail module.
export const dropGmailEvent = (
  event: ErrorEvent,
  hint: EventHint,
): ErrorEvent | null => {
  if (isGmailError(hint.originalException)) return null;
  const values = event.exception?.values ?? [];
  const fromGmail = values.some(
    (value) =>
      GMAIL_ERROR_NAMES.has(value.type ?? '') ||
      (value.stacktrace?.frames ?? []).some((frame) =>
        isGmailModuleFile(frame.filename ?? ''),
      ),
  );
  return fromGmail ? null : event;
};

export const dropGmailBreadcrumb = (
  breadcrumb: Breadcrumb,
): Breadcrumb | null => {
  const { category, data } = breadcrumb;
  if ((category === 'fetch' || category === 'xhr') && isGoogleUrl(data?.url)) {
    return null;
  }
  if (category === 'console') {
    const args: unknown[] = Array.isArray(data?.arguments)
      ? data.arguments
      : [];
    if ([breadcrumb.message, ...args].some(mentionsGmail)) return null;
  }
  return breadcrumb;
};

export const initSentry = () => {
  if (!env.VITE_SENTRY_DSN) {
    return;
  }
  Sentry.init({
    dsn: env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 0,
    sendDefaultPii: false,
    beforeSend: dropGmailEvent,
    beforeBreadcrumb: dropGmailBreadcrumb,
  });
};

// The router's `defaultOnCatch`; keeps the React component stack a bare capture would drop.
export const reportRouteError = (error: unknown, info: ErrorInfo): void => {
  Sentry.captureReactException(error, info);
};
